import * as Schema from "effect/Schema";

const number = (minimum: number, maximum: number) =>
  Schema.Number.check(Schema.isBetween({ minimum, maximum }));
const integer = (minimum: number, maximum: number) =>
  Schema.Int.check(Schema.isBetween({ minimum, maximum }));
export const MusicCompositionId = Schema.String.check(
  Schema.isPattern(/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,95}$/),
);
export const MusicCompositionScope = Schema.Struct({
  worldKey: Schema.String.check(Schema.isNonEmpty(), Schema.isMaxLength(200)),
  sessionId: Schema.String.check(Schema.isPattern(/^S_[a-zA-Z0-9_-]{1,100}$/)),
});
export type MusicCompositionScope = typeof MusicCompositionScope.Type;
export const MusicInstrument = Schema.Literals(["drums", "bass", "keys", "pad", "lead"]);
export type MusicInstrument = typeof MusicInstrument.Type;
export const MusicNote = Schema.Struct({
  pitch: integer(0, 127),
  beat: number(0, 128),
  duration: number(0.03125, 128),
  velocity: number(0.01, 1),
});
export type MusicNote = typeof MusicNote.Type;
export const MusicSoundSource = Schema.Struct({
  scope: MusicCompositionScope,
  projectId: MusicCompositionId,
  revision: integer(1, 1_000_000),
  assetId: MusicCompositionId,
  sha256: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/)),
});
export type MusicSoundSource = typeof MusicSoundSource.Type;
export const WorldMediaSource = Schema.Struct({
  kind: Schema.Literal("world-media"),
  worldKey: MusicCompositionScope.fields.worldKey,
  assetId: Schema.String.check(Schema.isPattern(/^ma_[a-f0-9]{64}$/)),
  sha256: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/)),
});
export type WorldMediaSource = typeof WorldMediaSource.Type;
export const MusicAudioSource = Schema.Union([MusicSoundSource, WorldMediaSource]);
export type MusicAudioSource = typeof MusicAudioSource.Type;
export const MusicAudioClip = Schema.Struct({
  assetId: MusicCompositionId,
  startSeconds: number(0, 86400),
  offsetSeconds: number(0, 86400),
  durationSeconds: number(0.001, 86400),
  source: Schema.optionalKey(MusicAudioSource),
});
export const MusicAudioAsset = Schema.Struct({
  assetId: MusicCompositionId,
  name: Schema.String,
  role: Schema.String,
  mimeType: Schema.String,
  bytes: integer(1, 2_000_000_000),
  sha256: Schema.String,
  sampleRate: integer(1, 384000),
  sampleCount: integer(1, 40_000_000_000),
  channels: integer(1, 32),
  durationSeconds: number(0.001, 86400),
  waveform: Schema.Array(number(0, 1)).check(Schema.isMaxLength(1024)),
});
export type MusicAudioAsset = typeof MusicAudioAsset.Type;
export type MusicLibrarySound = {
  readonly source: MusicSoundSource;
  readonly asset: MusicAudioAsset;
  readonly projectTitle: string;
};
export const MusicCompositionTrack = Schema.Struct({
  id: MusicCompositionId,
  name: Schema.String.check(Schema.isNonEmpty(), Schema.isMaxLength(80)),
  instrument: MusicInstrument,
  volume: number(0, 1),
  pan: number(-1, 1),
  mute: Schema.Boolean,
  solo: Schema.Boolean,
  cutoff: number(40, 20000),
  reverb: number(0, 0.75),
  delay: number(0, 0.75),
  audioClip: Schema.optionalKey(MusicAudioClip),
  notes: Schema.Array(MusicNote).check(Schema.isMaxLength(1024)),
});
export type MusicCompositionTrack = typeof MusicCompositionTrack.Type;
export const MusicCompositionDocument = Schema.Struct({
  format: Schema.Literal("neucleos-music-v1"),
  title: Schema.String.check(Schema.isNonEmpty(), Schema.isMaxLength(120)),
  bpm: integer(40, 240),
  bars: integer(1, 2048),
  beatsPerBar: Schema.Literal(4),
  audioAssets: Schema.optionalKey(Schema.Array(MusicAudioAsset)),
  tracks: Schema.Array(MusicCompositionTrack).check(Schema.isMinLength(1), Schema.isMaxLength(8)),
});
export type MusicCompositionDocument = typeof MusicCompositionDocument.Type;

/** Complete recorded timeline (plus a caller's explicit effect tail), without bar quantization. */
export function musicCompositionDuration(document: MusicCompositionDocument): number {
  return document.tracks.some((track) => track.audioClip)
    ? Math.max(
        ...document.tracks.map((track) =>
          track.audioClip
            ? track.audioClip.startSeconds + track.audioClip.durationSeconds
            : Math.max(
                0,
                ...track.notes.map((note) => ((note.beat + note.duration) * 60) / document.bpm),
              ),
        ),
      )
    : (document.bars * 4 * 60) / document.bpm;
}

export function musicCompositionIssue(document: MusicCompositionDocument): string | null {
  if (
    !document.tracks.some((track) => track.audioClip) &&
    (document.bars * 4 * 60) / document.bpm > 120
  )
    return "Keep this piece within two minutes, or increase its tempo.";
  if (new Set(document.tracks.map((track) => track.id)).size !== document.tracks.length)
    return "Each track needs its own identifier.";
  if (document.tracks.reduce((total, track) => total + track.notes.length, 0) > 2048)
    return "This piece supports up to 2,048 notes.";
  for (const track of document.tracks) {
    if (track.instrument !== "drums") {
      const ends = new Map<number, number>();
      for (const note of [...track.notes].sort((a, b) => a.beat - b.beat)) {
        if ((ends.get(note.pitch) ?? 0) > note.beat + 0.00001)
          return `Two notes of the same pitch overlap in ${track.name}. Shorten one or move it to another track.`;
        ends.set(note.pitch, note.beat + note.duration);
      }
    }
    if (track.notes.some((note) => note.beat + note.duration > document.bars * 4 + 0.00001))
      return `A note in ${track.name} extends beyond the piece. Add bars or shorten that note.`;
    if (
      track.instrument === "drums" &&
      track.notes.some((note) => ![36, 38, 42, 46].includes(note.pitch))
    )
      return "Drums use MIDI notes 36 (kick), 38 (snare), 42 (closed hi-hat) and 46 (open hi-hat).";
  }
  return null;
}

export const SaveMusicComposition = Schema.Struct({
  projectId: MusicCompositionId,
  expectedRevision: Schema.NullOr(integer(1, 1_000_000)),
  requestId: MusicCompositionId,
  document: MusicCompositionDocument,
});
export type SaveMusicComposition = typeof SaveMusicComposition.Type;
export const InsertMusicSound = Schema.Struct({
  projectId: MusicCompositionId,
  expectedRevision: integer(1, 1_000_000),
  requestId: MusicCompositionId,
  source: MusicAudioSource,
  startSeconds: number(0, 86400),
  targetTrackId: Schema.optionalKey(MusicCompositionId),
});
export type InsertMusicSound = typeof InsertMusicSound.Type;
export const MusicRenderFormat = Schema.Literals(["wav", "midi"]);
export const RenderMusicComposition = Schema.Struct({
  projectId: MusicCompositionId,
  trackId: Schema.optionalKey(MusicCompositionId),
  expectedRevision: integer(1, 1_000_000),
  requestId: MusicCompositionId,
  format: MusicRenderFormat,
});
export type RenderMusicComposition = typeof RenderMusicComposition.Type;
export const MusicRender = Schema.Struct({
  exportId: MusicCompositionId,
  trackId: Schema.optionalKey(MusicCompositionId),
  projectId: MusicCompositionId,
  revision: integer(1, 1_000_000),
  format: MusicRenderFormat,
  status: Schema.Literals(["pending", "complete"]),
  bytes: integer(0, 1_000_000_000),
  sha256: Schema.NullOr(Schema.String),
});
export type MusicRender = typeof MusicRender.Type;
export const MusicComposition = Schema.Struct({
  projectId: MusicCompositionId,
  revision: integer(1, 1_000_000),
  updatedAt: Schema.String,
  document: MusicCompositionDocument,
  renders: Schema.optionalKey(Schema.Array(MusicRender)),
});
export type MusicComposition = typeof MusicComposition.Type;
export const MusicAudioRequest = Schema.Union([
  Schema.Struct({
    action: Schema.Literal("audio.upload"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
    requestId: MusicCompositionId,
    expectedRevision: integer(1, 1_000_000),
    name: Schema.String.check(Schema.isMaxLength(160)),
    offset: integer(0, 2_000_000_000),
    totalBytes: integer(1, 2_000_000_000),
    base64: Schema.String.check(Schema.isMaxLength(1_400_000)),
  }),
  Schema.Struct({
    action: Schema.Literal("audio.retain"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
    requestId: MusicCompositionId,
    expectedRevision: integer(1, 1_000_000),
  }),
  Schema.Struct({
    action: Schema.Literal("audio.separate"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
    requestId: MusicCompositionId,
    expectedRevision: integer(1, 1_000_000),
  }),
  Schema.Struct({
    action: Schema.Literal("audio.job"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
    requestId: MusicCompositionId,
  }),
  Schema.Struct({
    action: Schema.Literal("audio.cancel"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
    requestId: MusicCompositionId,
  }),
  Schema.Struct({
    action: Schema.Literal("audio.read"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
    assetId: MusicCompositionId,
    offset: integer(0, 2_000_000_000),
    length: integer(1, 1_048_576),
  }),
]);
export type MusicAudioRequest = typeof MusicAudioRequest.Type;
export const MusicCompositionRequest = Schema.Union([
  MusicAudioRequest,
  Schema.Struct({ action: Schema.Literal("list"), scope: MusicCompositionScope }),
  Schema.Struct({ action: Schema.Literal("library"), scope: MusicCompositionScope }),
  Schema.Struct({ action: Schema.Literal("library.sounds"), scope: MusicCompositionScope }),
  Schema.Struct({
    action: Schema.Literal("sound.insert"),
    scope: MusicCompositionScope,
    ...InsertMusicSound.fields,
  }),
  Schema.Struct({
    action: Schema.Literal("project.import"),
    scope: MusicCompositionScope,
    sourceSessionId: MusicCompositionScope.fields.sessionId,
    projectId: MusicCompositionId,
    expectedRevision: integer(1, 1_000_000),
    requestId: MusicCompositionId,
  }),
  Schema.Struct({
    action: Schema.Literal("read"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
  }),
  Schema.Struct({
    action: Schema.Literal("save"),
    scope: MusicCompositionScope,
    ...SaveMusicComposition.fields,
  }),
  Schema.Struct({
    action: Schema.Literal("undo"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
    expectedRevision: integer(1, 1_000_000),
    requestId: MusicCompositionId,
  }),
  Schema.Struct({
    action: Schema.Literal("render"),
    scope: MusicCompositionScope,
    ...RenderMusicComposition.fields,
  }),
  Schema.Struct({
    action: Schema.Literal("render.read"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
    exportId: MusicCompositionId,
    includeBytes: Schema.optionalKey(Schema.Boolean),
    offset: Schema.optionalKey(integer(0, 1_000_000_000)),
    length: Schema.optionalKey(integer(1, 1_048_576)),
  }),
  Schema.Struct({
    action: Schema.Literal("render.chunk"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
    exportId: MusicCompositionId,
    offset: integer(0, 1_000_000_000),
    totalBytes: integer(1, 1_000_000_000),
    base64: Schema.String.check(Schema.isMaxLength(1_400_000)),
  }),
  Schema.Struct({
    action: Schema.Literal("render.complete"),
    scope: MusicCompositionScope,
    projectId: MusicCompositionId,
    exportId: MusicCompositionId,
    base64: Schema.String.check(
      Schema.isMaxLength(32 * 1024 * 1024),
      Schema.isPattern(/^[A-Za-z0-9+/]+={0,2}$/),
    ),
  }),
]);
export type MusicCompositionRequest = typeof MusicCompositionRequest.Type;
